import React from "react";
import { Button } from "../button/Button";
import { ActionChip, ChipPanel } from "../Chip";
import { colors } from "../colors/color";
import { Flex } from "./FlexHelpers";
import styled from "styled-components";
import { DefaultInputStateCss, DisabledInputStateCss } from "../inputs/sharedStyles";
import { InputMessage } from "../InputMessage";
import { TextInput } from "../inputs/TextInput";

type TagEditorProps = {
    availableTags:string[],
    selectedTags: string[],
    validator:(tag:string) => boolean,
    onSelectionChange:(tags: string[])=>void,
    disabled?: boolean,
    texts?: {
        selectTagOption:string,
        addnewTagOption:string,
        addTextPlaceholder:string,
        noTagsSelectedText:string
    }
};
export function TagEditor(props: TagEditorProps): JSX.Element
{
    const createNewTagOptionValue = "__$$create_new_tag$$__";

    const [newTag, setNewTag] = React.useState<{value:string, showError:boolean} | null>(null);

    function handleDeselectTag(tagValue: string)
    {
        if (props.disabled)
            return;

        setNewTag(null);
        props.onSelectionChange(props.selectedTags.filter((tag) => tag !== tagValue));
    }

    function handleSelectAvailableTag(tagValue: string)
    {
        if (props.disabled)
            return;
        
        if (!tagValue)
            return;

        if(props.selectedTags.includes(tagValue))
        {
            setNewTag(null);
            return;
        }

        if (tagValue === createNewTagOptionValue)
        {
            setNewTag({ value: "", showError:false });
        }
        else
        {
            setNewTag(null);
            props.onSelectionChange([...props.selectedTags, tagValue]);
        }
    }

    function handleClickAddNewTag()
    {
        if (props.disabled)
            return;

        if(!newTag)
            return;

        if(props.availableTags.includes(newTag.value))
        {
            setNewTag(null);
            return;
        }

        if(!props.validator(newTag.value))
        {
            setNewTag({ ...newTag, showError:true });
        }
        else
        {
            props.onSelectionChange([...props.selectedTags, newTag.value]);
            setNewTag(null);
        }
    }

    return (
        <Flex flexDirection="column" gap={12}>
            <ChipPanel>
                {props.selectedTags.map((tag) => (
                    <ActionChip
                        key={tag}
                        colorHash={tag}
                        trailingIcon="actions_close"
                        onClick={() => handleDeselectTag(tag)}>
                        {tag}
                    </ActionChip>
                ))}
            </ChipPanel>

            {props.selectedTags.length === 0 &&
                <i>{props.texts?.noTagsSelectedText ?? "Ingen tags valgt"}</i>
            }

            <Flex flexDirection="column" gap={8}>
                <Flex alignItems="flex-start" gap={8}>
                    <TagSelect 
                        value={newTag ? createNewTagOptionValue : ""} 
                        onChange={(event) => handleSelectAvailableTag(event.target.value)}>
                        <option value="">{props.texts?.selectTagOption ?? "Vælg tag"}</option>
                        {props.availableTags.sort((a, b) => a.localeCompare(b)).filter(t => !props.selectedTags.includes(t)).map((tag) => (
                            <option key={tag} value={tag}>{tag}</option>
                        ))}
                        <option value={createNewTagOptionValue}>{props.texts?.addnewTagOption ?? "Opret nyt tag..."}</option>
                    </TagSelect>
                </Flex>

                {newTag &&
                    <Flex alignItems="flex-end" gap={8}>
                        <TextInput
                            placeholder={props.texts?.addTextPlaceholder ?? "Nyt tag..."}
                            inErrorState={newTag.showError}
                            value={newTag.value}
                            disabled={props.disabled}
                            onChange={(value) => setNewTag({ ...newTag, value })}
                            onKeyDown={(event) => {
                                if (event.key === "Enter")
                                {
                                    event.preventDefault();
                                    handleClickAddNewTag();
                                }
                            }} />
                        <Button
                            variant="text"
                            onClick={handleClickAddNewTag}
                            text="Opret"
                            disabled={props.disabled || !newTag.value.trim()} />
                        
                        {newTag.showError &&
                            <InputMessage inErrorState={true}>Ugyldig værdi</InputMessage>
                        }
                    </Flex>
                }
            </Flex>
        </Flex>
    );
}

const TagSelect = styled.select`
    min-width: 200px;
    min-height: 36px;
    padding: 7px 10px;
    border: 1px solid ${colors.pill.border};
    border-radius: 4px;
    background: ${colors.background.white};
    color: ${colors.text.normal};
    font-family: "Noto Sans", sans-serif;
    font-size: 14px;
    line-height: 20px;
    outline: none;
    transition: border-color 120ms ease, box-shadow 120ms ease, background-color 120ms ease;


    &:hover {
        ${DefaultInputStateCss.hover}
    }
    &:focus {
        ${DefaultInputStateCss.focus}
    }
    &:active {
        ${DefaultInputStateCss.active}
    }
    &:disabled {
        ${DisabledInputStateCss.container}
        ${DisabledInputStateCss.text}
    }
`;